import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, CheckCircle, XCircle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from '../contexts/CurrencyContext';

const PayPalPayment = ({ 
  amount, // Amount in USD (base currency)
  paymentType, // "booth_fee" or "buyer_registration"
  referenceId, // booth_id or registration_id (optional for new registrations)
  description,
  pendingBoothData, // For new booth creation - data to create booth after payment
  pendingEventId,   // For new buyer registration - event ID to register after payment
  couponCode,       // Coupon code applied to discount the amount
  onSuccess,
  onError,
  onCancel,
  // For custom order creation (like list purchases)
  createOrder: customCreateOrder,
  onApprove: customOnApprove
}) => {
  const [clientId, setClientId] = useState('');
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, processing, completed, failed
  const { currency, getCurrencyCode, getConvertedAmount, formatPrice } = useCurrency();

  useEffect(() => {
    fetchPayPalConfig();
  }, []);

  const fetchPayPalConfig = async () => {
    try {
      // Always fetch from API to respect admin dashboard sandbox/live toggle
      const res = await axios.get(`${API}/payments/config`);
      setClientId(res.data.client_id);
    } catch (error) {
      console.error('Failed to fetch PayPal config:', error);
      // Fallback to env var if API fails
      const envClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
      if (envClientId) {
        setClientId(envClientId);
      } else {
        toast.error('Failed to initialize payment system');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get the amount in the selected currency
  const displayAmount = getConvertedAmount(amount);
  const currencyCode = getCurrencyCode();

  const createOrder = async () => {
    // If custom createOrder is provided, use it
    if (customCreateOrder) {
      return customCreateOrder();
    }
    
    try {
      const orderData = {
        amount: displayAmount, // Send converted amount
        currency_code: currencyCode, // Send selected currency
        payment_type: paymentType,
        description: description
      };
      
      // For existing booth/registration, include reference_id
      if (referenceId) {
        orderData.reference_id = referenceId;
      }
      
      // For NEW booth creation, include pending booth data
      if (pendingBoothData) {
        orderData.pending_booth_data = pendingBoothData;
      }
      
      // For NEW buyer registration, include pending event ID
      if (pendingEventId) {
        orderData.pending_event_id = pendingEventId;
      }
      
      // Include coupon code if applied
      if (couponCode) {
        orderData.coupon_code = couponCode;
      }
      
      const response = await axios.post(`${API}/payments/create-order`, orderData);
      return response.data.order_id;
    } catch (error) {
      console.error('Failed to create order:', error);
      setPaymentStatus('failed');
      toast.error('Failed to create payment order');
      throw error;
    }
  };

  const onApprove = async (data) => {
    // If custom onApprove is provided, use it
    if (customOnApprove) {
      try {
        setPaymentStatus('processing');
        toast.loading('Processing your payment...');
        await customOnApprove(data);
        toast.dismiss();
        setPaymentStatus('completed');
        return;
      } catch (error) {
        toast.dismiss();
        setPaymentStatus('failed');
        toast.error('Payment processing failed');
        return;
      }
    }
    
    try {
      setPaymentStatus('processing');
      toast.loading('Processing your payment...');
      const response = await axios.post(`${API}/payments/capture/${data.orderID}`);
      toast.dismiss();
      
      if (response.data.status === 'COMPLETED') {
        setPaymentStatus('completed');
        toast.success('Payment successful!');
        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        setPaymentStatus('failed');
        toast.error('Payment was not completed');
        if (onError) {
          onError(new Error('Payment not completed'));
        }
      }
    } catch (error) {
      toast.dismiss();
      console.error('Failed to capture payment:', error);
      setPaymentStatus('failed');
      toast.error('Failed to process payment');
      if (onError) {
        onError(error);
      }
    }
  };

  const handleError = (error) => {
    console.error('PayPal error:', error);
    setPaymentStatus('failed');
    toast.error('Payment failed');
    if (onError) {
      onError(error);
    }
  };

  const handleCancel = () => {
    setPaymentStatus('pending');
    toast.info('Payment cancelled');
    if (onCancel) {
      onCancel();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-2 text-sm text-slate-600">Initializing payment...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!clientId) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center text-center">
            <XCircle className="h-12 w-12 text-red-500" />
            <p className="mt-2 text-sm text-red-600">Payment system unavailable</p>
            <p className="text-xs text-slate-500 mt-1">Please try again later or contact support</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (paymentStatus === 'completed') {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center text-center">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <h3 className="mt-3 text-lg font-semibold text-green-700">Payment Successful!</h3>
            <p className="text-sm text-green-600 mt-1">Your payment of {formatPrice(amount)} has been processed.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Secure Payment</CardTitle>
          </div>
          <Badge className="bg-blue-100 text-blue-700">PayPal</Badge>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-slate-600">Amount Due:</span>
            <span className="text-2xl font-bold text-primary">{formatPrice(amount)}</span>
          </div>
          {currency !== 'USD' && (
            <p className="text-xs text-slate-500 mt-1 text-right">
              (Base price: ${amount.toFixed(2)} USD)
            </p>
          )}
        </div>

        {paymentStatus === 'processing' ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-2 text-sm text-slate-600">Processing payment...</p>
          </div>
        ) : (
          <PayPalScriptProvider options={{ 
            clientId: clientId,
            currency: currencyCode,
            intent: "capture",
            components: "buttons,funding-eligibility",
            "enable-funding": "paylater,card",
            "disable-funding": ""
          }}>
            <PayPalButtons
              key={`${currencyCode}-${displayAmount}`} // Re-render when currency changes
              style={{
                layout: "vertical",
                color: "gold",
                shape: "rect",
                label: "paypal",
                height: 45
              }}
              fundingSource={undefined}
              createOrder={createOrder}
              onApprove={onApprove}
              onError={handleError}
              onCancel={handleCancel}
            />
          </PayPalScriptProvider>
        )}

        <p className="text-xs text-slate-500 text-center mt-4">
          Secure payment powered by PayPal. Your financial information is never stored on our servers.
        </p>
      </CardContent>
    </Card>
  );
};

export default PayPalPayment;
